/**
 * WebSocket 同步通道管理
 * - 自动重连 (指数退避)
 * - 心跳 (30s ping)
 * - 监听 {type: 'change'} 消息, 回调给订阅者
 * - 收到 {type: 'kicked'} 立即断连 + 通知上层
 */

import { getToken } from '../api/client';

export type SyncMessage =
  | {
      type: 'change';
      entity: 'list' | 'task' | 'step';
      op: 'create' | 'update' | 'delete';
      id: string;
      version?: number;
      ts: number;
      /** reminder 触发的 update 会带这个, 前端用来弹弹窗 */
      reminder?: {
        title: string;
        body?: string;
        kind: 'reminder' | 'due_date';
        urgency: 'normal' | 'strong';
        task_id: string;
      };
    }
  | { type: 'kicked'; reason: string }
  | { type: 'pong'; ts: number }
  | { type: 'error'; error: string };

export type SyncState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'offline';

export type Listener = (msg: SyncMessage) => void;
export type StateListener = (state: SyncState) => void;

class SyncSocket {
  private ws: WebSocket | null = null;
  private state: SyncState = 'idle';
  private listeners = new Set<Listener>();
  private stateListeners = new Set<StateListener>();
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private intentionallyClosed = false;
  private currentToken: string | null = null;

  getState(): SyncState {
    return this.state;
  }

  private setState(s: SyncState): void {
    if (this.state === s) return;
    this.state = s;
    for (const l of this.stateListeners) l(s);
  }

  onMessage(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  onState(fn: StateListener): () => void {
    this.stateListeners.add(fn);
    fn(this.state);
    return () => this.stateListeners.delete(fn);
  }

  /** 启动连接 (或重连) */
  connect(token: string): void {
    if (this.ws && this.currentToken === token) return;
    this.disconnect();
    this.currentToken = token;
    this.intentionallyClosed = false;
    this.open();
  }

  /** 断开连接 (但保留 listener) */
  disconnect(): void {
    this.intentionallyClosed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close(1000, 'client_disconnect');
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    this.setState('idle');
  }

  private open(): void {
    const token = this.currentToken;
    if (!token) return;

    this.setState(this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting');

    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/api/ws?token=${encodeURIComponent(token)}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.reconnectAttempt = 0;
      this.setState('connected');
      this.startHeartbeat();
    });

    ws.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as SyncMessage;
        for (const l of this.listeners) l(msg);
        if (msg.type === 'kicked') {
          // 被踢, 立即关掉
          this.disconnect();
        }
      } catch {
        // 忽略无效消息
      }
    });

    ws.addEventListener('close', (ev) => {
      this.stopHeartbeat();
      this.ws = null;
      if (this.intentionallyClosed) return;
      // 4401 / 4403: token 问题, 不重连
      if (ev.code === 4401 || ev.code === 4403) {
        this.setState('offline');
        return;
      }
      this.scheduleReconnect();
    });

    ws.addEventListener('error', () => {
      // close 事件会处理重连
    });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.intentionallyClosed) return;
    this.setState('reconnecting');
    this.reconnectAttempt++;
    // 指数退避: 1s, 2s, 4s, 8s ... 最大 30s
    const delay = Math.min(30_000, 1000 * Math.pow(2, this.reconnectAttempt - 1));
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      // 重连前重新拿 token (可能被清掉了)
      const t = getToken();
      if (!t) {
        this.setState('offline');
        return;
      }
      this.currentToken = t;
      this.open();
    }, delay);
  }
}

export const syncSocket = new SyncSocket();